import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { useDeleteCustomAttribute } from '../../hooks/custom-attributes/use-delete-custom-attribute';
import { CustomAttributeRowFormData } from '../../validations/custom-attribute-validation';

interface DeleteCustomAttributeModalProps {
  attribute: CustomAttributeRowFormData | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const I18N = 'pages.membersCustomAttributes';

/**
 * Confirmation modal for deleting a custom attribute definition.
 *
 * Unlike departments/roles/seniorities there is no transfer step — the attribute is
 * simply removed and team members lose this categorization.
 */
export function DeleteCustomAttributeModal({
  attribute,
  open,
  onClose,
  onSuccess,
}: DeleteCustomAttributeModalProps) {
  const { t } = useTranslation('authenticated.settings');
  const deleteMutation = useDeleteCustomAttribute();

  const handleDelete = async (closeModal: () => void) => {
    if (!attribute) return;

    try {
      await deleteMutation.mutateAsync(attribute.attributeId);
      closeModal();
      displayToastSuccess(t(`${I18N}.deleteModal.successDescription`, { name: attribute.name }));
      onSuccess?.();
    } catch {
      // Error surfaced by the mutation's onError handler.
    }
  };

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      title={t(`${I18N}.deleteModal.title`)}
      message={t(`${I18N}.deleteModal.message`)}
      confirmText={t(`${I18N}.deleteModal.delete`)}
      cancelText={t(`${I18N}.deleteModal.cancel`)}
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteMutation.isPending}
    />
  );
}
